import { clampRange } from '../util/math.js';
import visualTuning from '../../../data/cpu-visual-polish.json';

export type CameraMotionOptions = {
  responseRate: number;
  maxSpeed: number;
  maxAcceleration: number;
};

export type CameraMotionResult = {
  position: number;
  velocity: number;
};

export type PuntCameraPhase = 'changeover' | 'flight' | 'return';

export function puntCameraMotionOptions(phase: PuntCameraPhase): CameraMotionOptions {
  return visualTuning.puntCamera[phase];
}

/** Keeps a punt framed around the recorded football through flight and return. */
export function puntBallCameraTargetX(
  ballX: number,
  viewportWidth: number,
  worldWidth: number,
): number {
  const safeViewportWidth = Math.max(0, Number.isFinite(viewportWidth) ? viewportWidth : 0);
  const maximumCameraX = Math.max(0, (Number.isFinite(worldWidth) ? worldWidth : safeViewportWidth) - safeViewportWidth);
  const safeBallX = Number.isFinite(ballX) ? ballX : safeViewportWidth * 0.5;
  return clampRange(safeBallX - safeViewportWidth * 0.5, 0, maximumCameraX);
}

/**
 * Advances a camera without allowing its pan velocity to change instantly.
 *
 * Position-only easing can reverse direction in one rendered frame when the
 * focus target changes. Keeping velocity as part of the presentation state
 * gives long special-teams pans a brief, comfortable deceleration instead.
 */
export function advanceComfortCamera(
  position: number,
  target: number,
  velocity: number,
  elapsedMs: number,
  options: CameraMotionOptions,
): CameraMotionResult {
  const deltaSeconds = clampRange(elapsedMs / 1000, 0.004, 0.04);
  const safePosition = Number.isFinite(position) ? position : target;
  const safeVelocity = Number.isFinite(velocity) ? velocity : 0;
  const error = target - safePosition;

  if (Math.abs(error) < 0.05 && Math.abs(safeVelocity) < 0.5) {
    return { position: target, velocity: 0 };
  }

  const desiredVelocity = clampRange(
    error * Math.max(0, options.responseRate),
    -Math.max(0, options.maxSpeed),
    Math.max(0, options.maxSpeed),
  );
  const maxVelocityChange = Math.max(0, options.maxAcceleration) * deltaSeconds;
  const nextVelocity = safeVelocity + clampRange(
    desiredVelocity - safeVelocity,
    -maxVelocityChange,
    maxVelocityChange,
  );
  const nextPosition = safePosition + nextVelocity * deltaSeconds;
  const crossedTarget = error !== 0 && Math.sign(target - nextPosition) !== Math.sign(error);

  if (crossedTarget) {
    return { position: target, velocity: 0 };
  }

  return {
    position: nextPosition,
    velocity: nextVelocity,
  };
}
